import { Injectable } from "@angular/core";
import { AppEvent, AppEventId, LocalStorageEvent } from "@loader-event-bus/app-event.model";
import { EventBus } from "./event-bus";
import { getResolvedInitEvent } from "./init-event-resolver";

/**
 * An event bus that allows communication between different parts of the application.
 *
 * It uses MessageChannel to send messages.
 * This implementation is used in the child iframe to communicate with the loader-factory.
 *
 * It waits for the init event to be received and sends messages through the event bus channel via
 * the port that has been communicated through the init event.
 */
@Injectable({ providedIn: "root" })
export class EventBusClient implements EventBus {
  // The port used to send messages
  private _port: MessagePort | null = null;

  set port(value: MessagePort) {
    this._port = value;
  }

  // List of subscribers to the event bus
  subscribers: Array<(value: AppEvent) => void> = [];

  /**
   * Waits for the init event to be received.
   *
   * Optionally, a callback can be provided to handle the init event.
   * @param callback - The callback function to be called when the init event is received.
   * @returns A promise that resolves when the init event is received.
   */
  waitForInit(callback?: (event: AppEvent) => void): Promise<void> {
    const preResolved = getResolvedInitEvent();
    if (preResolved) {
      this.port = preResolved.port;
      this.sendEventMessage({ eventId: AppEventId.INIT_ACK, data: null });
      if (callback) this.subscribe(callback);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      window.addEventListener("message", (event) => {
        if (!event.ports.length) return;

        const message = event.data as AppEvent;
        if (message.eventId === AppEventId.INIT) {
          if (!this.port) {
            this.port = event.ports[0];
          }
        }

        const ackMessage: AppEvent = { eventId: AppEventId.INIT_ACK, data: null };
        this.sendEventMessage(ackMessage);

        if (callback) this.subscribe((message) => callback(message));
        resolve();
      });
    });
  }

  public sendEventMessage(message: AppEvent | LocalStorageEvent<unknown>): void {
    this._port?.postMessage(message);
  }

  public subscribe(callback: (value: AppEvent) => void): void {
    this.subscribers.push(callback);

    const handler = (event: MessageEvent) => {
      const message = event.data as AppEvent;
      this.subscribers.forEach((subscriber) => subscriber(message));
    };

    if (this._port) this._port.onmessage = handler;
  }

  public closeAllChannels(): void {
    this._port?.close();
  }
}
